import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { useMemo, useState } from 'react'
import { Tab, Tabs } from 'react-bootstrap'
import ECharts from '~/components/echarts'
import FootNote from '~/components/footnote'
import AWSDashboardCostCategoriesFilters from '~/dashboard/aws_dashboard_cost_categories_filters'
import AWSDashboardFiltersService from '~/dashboard/aws_dashboard_filters_service'
import AWSDashboardFiltersServiceUsageTypeDaily from '~/dashboard/aws_dashboard_filters_usage_type_daily'
import AWSDashboardFiltersServiceUsageTypeGroup from '~/dashboard/aws_dashboard_filters_usage_type_group'
import {
  awsDailySpendByServiceChartPath,
  awsDailySpendByUsageTypeChartPath,
  awsMonthlySpendByAccountChartPath,
  awsMonthlySpendByCostCategoryChartPath,
  awsMonthlySpendByPurchaseTypeChartPath,
  awsMonthlySpendByRegionChartPath,
  awsMonthlySpendByServiceChartPath,
  awsMonthlySpendByUsageTypeGroupByAccountChartPath,
  awsMonthlySpendByUsageTypeGroupByRegionChartPath,
  awsMonthlySpendByUsageTypeGroupChartPath,
  awsPreviousMonthSpendByServiceChartPath,
  awsPreviousMonthSpendByUsageTypeGroupChartPath,
} from '~/routes'

export default function AWSDashboardCharts({ organizationId: orgId, hasCostCategories }) {
  const [serviceChartParams, setServiceChartParams] = useState({})
  const [usageTypeGroupChartParams, setUsageTypeGroupChartParams] = useState({})
  const [dailyChartParams, setDailyChartParams] = useState({})
  const [costCategoryChartParams, setCostCategoryChartParams] = useState({})

  const [openedTabs, setOpenedTabs] = useState(['main-tab'])
  const [activeTab, setActiveTab] = useState('main-tab')

  const handleTabChange = (tabName) => {
    setActiveTab(tabName)

    // Lazy load charts
    if (!openedTabs.includes(tabName)) {
      setOpenedTabs((prevOpenedTabs) => [...prevOpenedTabs, tabName])
    }
  }

  const openedTab = (tabName) => {
    return openedTabs.includes(tabName)
  }

  // memoize URLs to prevent rerenders
  const awsPreviousMonthSpendByServiceUrl = useMemo(() => awsPreviousMonthSpendByServiceChartPath(orgId), [orgId])
  const awsPreviousMonthSpendByUsageTypeGroupUrl = useMemo(
    () => awsPreviousMonthSpendByUsageTypeGroupChartPath(orgId),
    [orgId],
  )
  const awsMonthlySpendByServiceChartUrl = useMemo(() => awsMonthlySpendByServiceChartPath(orgId), [orgId])
  const awsDailySpendByServiceChartUrl = useMemo(() => awsDailySpendByServiceChartPath(orgId), [orgId])
  const awsDailySpendByUsageTypeChartUrl = useMemo(() => awsDailySpendByUsageTypeChartPath(orgId), [orgId])
  const awsMonthlySpendByRegionChartUrl = useMemo(() => awsMonthlySpendByRegionChartPath(orgId), [orgId])
  const awsMonthlySpendByAccountChartUrl = useMemo(() => awsMonthlySpendByAccountChartPath(orgId), [orgId])
  const awsMonthlySpendByPurchaseTypeChartUrl = useMemo(() => awsMonthlySpendByPurchaseTypeChartPath(orgId), [orgId])
  const awsMonthlySpendByCostCategoryChartUrl = useMemo(() => awsMonthlySpendByCostCategoryChartPath(orgId), [orgId])
  const awsMonthlySpendByUsageTypeGroupChartUrl = useMemo(
    () => awsMonthlySpendByUsageTypeGroupChartPath(orgId),
    [orgId],
  )
  const awsMonthlySpendByUsageTypeGroupByRegionChartUrl = useMemo(
    () => awsMonthlySpendByUsageTypeGroupByRegionChartPath(orgId),
    [orgId],
  )
  const awsMonthlySpendByUsageTypeGroupByAccountChartUrl = useMemo(
    () => awsMonthlySpendByUsageTypeGroupByAccountChartPath(orgId),
    [orgId],
  )

  return (
    <div className='card-tabs'>
      <Tabs id='controlled-tabs' activeKey={activeTab} onSelect={(k) => handleTabChange(k)}>
        <Tab eventKey='main-tab' title={t`Main Spend`}>
          <div className='tab-content'>
            <div className='card tab-pane active show'>
              <div className='card-body'>
                <AWSDashboardFiltersService organizationId={orgId} setChartParams={setServiceChartParams} />
                {openedTab('main-tab') && (
                  <div className='row gy-4'>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous month spend by service`}
                        chartInfoTitle='aws_previous_month_spend_by_service'
                        url={awsPreviousMonthSpendByServiceUrl}
                        params={serviceChartParams}
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 18 months spend by service`}
                        chartInfoTitle='aws_previous_18_months_spend_by_service'
                        url={awsMonthlySpendByServiceChartUrl}
                        params={serviceChartParams}
                        showExportButton
                      />
                    </div>

                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 18 months spend by region`}
                        chartInfoTitle='aws_previous_18_months_spend_by_region'
                        url={awsMonthlySpendByRegionChartUrl}
                        params={serviceChartParams}
                        showExportButton
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 18 months spend by account`}
                        chartInfoTitle='aws_previous_18_months_spend_by_account'
                        url={awsMonthlySpendByAccountChartUrl}
                        params={serviceChartParams}
                        showExportButton
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 18 months spend by purchase type`}
                        chartInfoTitle='aws_previous_18_months_spend_by_purchase_type'
                        url={awsMonthlySpendByPurchaseTypeChartUrl}
                        params={serviceChartParams}
                        showExportButton
                      />
                    </div>
                  </div>
                )}
                <div className='row'>
                  <FootNote />
                </div>
              </div>
            </div>
          </div>
        </Tab>
        <Tab eventKey='usage-type-group-tab' title={t`Usage Type Group Spend`}>
          <div className='tab-content'>
            <div className='card tab-pane active show'>
              <div className='card-body'>
                <AWSDashboardFiltersServiceUsageTypeGroup
                  organizationId={orgId}
                  setChartParams={setUsageTypeGroupChartParams}
                />
                {openedTab('usage-type-group-tab') && (
                  <div className='row gy-4'>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous month spend by usage type group`}
                        chartInfoTitle='aws_previous_month_spend_by_usage_type_group'
                        url={awsPreviousMonthSpendByUsageTypeGroupUrl}
                        params={usageTypeGroupChartParams}
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 18 months spend by usage type group`}
                        chartInfoTitle='aws_previous_18_months_spend_by_usage_type_group'
                        url={awsMonthlySpendByUsageTypeGroupChartUrl}
                        params={usageTypeGroupChartParams}
                        showExportButton
                      />
                    </div>

                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 18 months spend by region`}
                        chartInfoTitle='aws_previous_18_months_spend_by_usage_type_group_by_region'
                        url={awsMonthlySpendByUsageTypeGroupByRegionChartUrl}
                        params={usageTypeGroupChartParams}
                        showExportButton
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 18 months spend by account`}
                        chartInfoTitle='aws_previous_18_months_spend_by_usage_type_group_by_account'
                        url={awsMonthlySpendByUsageTypeGroupByAccountChartUrl}
                        params={usageTypeGroupChartParams}
                        showExportButton
                      />
                    </div>
                  </div>
                )}
                <div className='row'>
                  <FootNote />
                </div>
              </div>
            </div>
          </div>
        </Tab>
        <Tab eventKey='daily-tab' title={t`Daily Spend`}>
          <div className='tab-content'>
            <div className='card tab-pane active show'>
              <div className='card-body'>
                <AWSDashboardFiltersServiceUsageTypeDaily organizationId={orgId} setChartParams={setDailyChartParams} />
                {openedTab('daily-tab') && (
                  <div className='row gy-4'>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 30 days spend by service *`}
                        chartInfoTitle='aws_previous_30_days_spend_by_service'
                        url={awsDailySpendByServiceChartUrl}
                        params={dailyChartParams}
                        showExportButton
                      />
                    </div>
                    <div className='col-sm-6'>
                      <ECharts
                        title={t`Previous 30 days spend by usage type *`}
                        chartInfoTitle='aws_previous_30_days_spend_by_usage_type'
                        url={awsDailySpendByUsageTypeChartUrl}
                        params={dailyChartParams}
                        showExportButton
                      />
                    </div>
                  </div>
                )}
                <div className='row'>
                  <FootNote />
                </div>
              </div>
            </div>
          </div>
        </Tab>

        {hasCostCategories && (
          <Tab eventKey='cost-category-monthly-tab' title={t`Cost Category Spend`}>
            <div className='tab-content'>
              <div className='card tab-pane active show'>
                <div className='card-body'>
                  {openedTab('cost-category-monthly-tab') && (
                    <>
                      <AWSDashboardCostCategoriesFilters
                        organizationId={orgId}
                        setChartParams={setCostCategoryChartParams}
                      />
                      <div className='row gy-4'>
                        <div className='col-sm-6'>
                          {!costCategoryChartParams.awsCostCategory && (
                            <div className='alert alert-warning alert-dismissible mt-3' role='alert'>
                              <Trans>
                                No data to display. Please select a Cost Category in the filters to view results.
                              </Trans>
                            </div>
                          )}
                          <ECharts
                            title={t`Previous 18 months spend by Cost Category`}
                            chartInfoTitle='aws_previous_18_months_spend_by_cost_category'
                            url={awsMonthlySpendByCostCategoryChartUrl}
                            params={costCategoryChartParams}
                            showExportButton
                          />
                        </div>
                      </div>
                    </>
                  )}
                  <div className='row'>
                    <FootNote />
                  </div>
                </div>
              </div>
            </div>
          </Tab>
        )}
      </Tabs>
    </div>
  )
}
